import React, { forwardRef, useEffect, useRef, useState } from 'react'
import { Button } from 'chemistry'
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  useOutsideClick
} from '@chakra-ui/react'

import FilterIcon from '../../FilterButton/FilterIcon'
import { ListFilterItems } from '../ListFilter/ListFilterItems'
import { RangeFilterItem } from '../RangeFilter'
import { CheckListFilterItems } from '../CheckListFilter/CheckListFilterItems'
import { useIsFullyInViewport } from './useIsFullyInViewport'

export const FILTERS_TOP_ID = 'filters_top'
export const FILTER_TOP_BUTTON_HEIGHT = 40
const filterTopButtonStyles = {
  display: 'flex',
  alignItems: 'center',
  height: `${FILTER_TOP_BUTTON_HEIGHT}px`,
  p: '6px 12px',
  fontSize: '14px',
  lineHeight: '21px',
  letterSpacing: '0.25px',
  color: 'grey.900',
  borderRadius: 'lg',
  gap: '8px',
  fontWeight: '600',
  bgColor: 'grey.50',
  _hover: {
    bgColor: 'grey.300'
  }
}

export const AllFiltersTopButton = ({ children, isShown, ...props }) => (
  <Button
    {...{
      ...filterTopButtonStyles,
      border: '1px solid rgb(65,63,55)',
      visibility: isShown ? 'visible' : 'hidden'
    }}
    variant='tertiary'
    position='static'
    {...props}
  >
    {children}
    <FilterIcon width='16px' height='16px' />
  </Button>
)

function FilterTopDropdownContents({
  filter,
  active,
  onChange,
  preventClick,
  rangeFilterToText,
  buttonActiveText,
  buttonInactiveText,
  format,
  lang,
  isParentCollapsed
}) {
  if (filter.items?.length === 0 && filter.type !== 'range') return null
  if (filter.type === 'list') {
    return (
      <ListFilterItems
        filter={filter}
        preventClick={preventClick}
        onChange={onChange}
        buttonActiveText={buttonActiveText}
        buttonInactiveText={buttonInactiveText}
        isParentCollapsed={isParentCollapsed}
        isDropdownFilter
        filterPosition='top'
        px='8px'
      />
    )
  }
  if (filter.type === 'range') {
    return (
      <RangeFilterItem
        filter={filter}
        active={active[filter.title]}
        toText={rangeFilterToText}
        onChange={onChange}
        filterPosition='top'
      />
    )
  }
  return (
    <CheckListFilterItems
      onChange={onChange}
      filter={filter}
      format={format}
      lang={lang}
      buttonActiveText={buttonActiveText}
      buttonInactiveText={buttonInactiveText}
      isParentCollapsed={isParentCollapsed}
      isDropdownFilter
      filterPosition='top'
    />
  )
}

export const FilterTopDropdownButton = forwardRef(
  ({ id, dropdownButtonRef, filter, children, setIsDropdownExpanded, ...props }, ref) => {
    return (
      <Accordion ref={ref} id={id} allowToggle {...props}>
        <AccordionItem border='none'>
          {({ isExpanded }) => {
            if (setIsDropdownExpanded) {
              setIsDropdownExpanded(isExpanded)
            }
            return (
              <>
                <AccordionButton
                  ref={dropdownButtonRef}
                  {...filterTopButtonStyles}
                  bgColor={isExpanded ? 'grey.500' : 'grey.100'}
                >
                  {filter.title.toUpperCase()}
                  <AccordionIcon color='grey.900' />
                </AccordionButton>
                {children}
              </>
            )
          }}
        </AccordionItem>
      </Accordion>
    )
  }
)

export function FilterTopDropdown({
  id,
  filter,
  active,
  filterProcessedData: {
    onChange,
    preventClick,
    format,
    lang,
    buttonActiveText,
    buttonInactiveText,
    rangeFilterToText
  },
  maxDropdownHeight,
  ...props
}) {
  const [isDropdownExpanded, setIsDropdownExpanded] = useState(false)
  const ref = useRef()
  const dropdownButtonRef = useRef()
  const dropdownPanelRef = useRef()
  const { isFullyInViewport, setIsFullyInViewport } = useIsFullyInViewport(dropdownPanelRef)
  useOutsideClick({
    ref,
    handler: () => {
      if (isDropdownExpanded) {
        dropdownButtonRef?.current?.click()
      }
    }
  })

  useEffect(() => {
    if (!isFullyInViewport) {
      document.getElementById(FILTERS_TOP_ID).scrollIntoView({ behavior: 'smooth' })
      setIsFullyInViewport(true)
    }
  }, [isFullyInViewport, setIsFullyInViewport])

  useEffect(() => {
    const container = document.querySelector(`#${id}`)
    if (!container) return

    const keyDownHandler = e => {
      if (e.key === 'Escape' && isDropdownExpanded) {
        e.preventDefault()
        dropdownButtonRef?.current?.click()
        document.querySelector(`#${id} button`).focus()
      }
    }

    container?.addEventListener('keydown', keyDownHandler)
    return () => {
      container?.removeEventListener('keydown', keyDownHandler)
    }
  }, [id, isDropdownExpanded, setIsDropdownExpanded])

  return (
    <FilterTopDropdownButton
      ref={ref}
      id={id}
      setIsDropdownExpanded={setIsDropdownExpanded}
      dropdownButtonRef={dropdownButtonRef}
      filter={filter}
      {...props}
    >
      <AccordionPanel
        ref={dropdownPanelRef}
        p='8px'
        position='absolute'
        bg='white.0'
        marginTop='8px'
        borderRadius='lg'
        minWidth='296px'
        border='1px solid'
        borderColor='grey.100'
        boxShadow='0px 4px 6px -2px rgba(0, 0, 0, 0.15), 0px 10px 15px -3px rgba(0, 0, 0, 0.30)'
        maxHeight={maxDropdownHeight}
        overflowY='auto'
        overscrollBehaviorY='contain'
      >
        <FilterTopDropdownContents
          filter={filter}
          active={active}
          onChange={onChange}
          preventClick={preventClick}
          rangeFilterToText={rangeFilterToText}
          buttonActiveText={buttonActiveText}
          buttonInactiveText={buttonInactiveText}
          format={format}
          lang={lang}
          isParentCollapsed={!isDropdownExpanded}
        />
      </AccordionPanel>
    </FilterTopDropdownButton>
  )
}
